import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Trash2,
  Edit2,
  Share2,
  CheckCircle,
  Clock,
  AlertCircle,
  Shield,
  Calendar,
  User,
  FileText,
  Wallet,
  X
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { deleteEntry, getCustomerLedger } from '../../services/dashboardApi';
import EditEntryModal from './EditEntryModal';

const EntryDetailsView = ({ entry, party, onBack, onRefresh }) => {
  const { t } = useLang();
  const [deleting, setDeleting] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [detailedEntry, setDetailedEntry] = useState(entry);

  useEffect(() => {
    setDetailedEntry(entry);
    if (entry?.id && party?.id) {
      let active = true;
      const fetchDetails = async () => {
        try {
          const res = await getCustomerLedger(party.id);
          if (active && res?.success && res.data?.entries) {
            const found = res.data.entries.find(e => String(e.id) === String(entry.id));
            if (found) {
              setDetailedEntry(prev => ({
                ...prev,
                ...found
              }));
            }
          }
        } catch (err) {
          console.error("Failed to fetch customer ledger for entry details:", err);
        }
      };
      fetchDetails();
      return () => {
        active = false;
      };
    }
  }, [entry, party?.id]);

  const isGot = detailedEntry.entry_type === 'get';
  const amount = parseFloat(detailedEntry.amount || 0);
  const runningBal = parseFloat(detailedEntry.running_balance || 0);

  // Robust Date & Time Parsing (Matches Mobile App Logic)
  const parseEntryDateTime = (entryDate, createdAt) => {
    if (!entryDate) return { date: '', time: '' };

    // Use entry_date for the date part
    const datePart = new Date(entryDate.replace(' ', 'T'));
    const displayDate = datePart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    // Use created_at for the accurate time part
    const timeSource = createdAt || entryDate;
    const timePart = new Date(timeSource.replace(' ', 'T'));
    const displayTime = timePart.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    return { date: displayDate, time: displayTime };
  };

  const { date: displayDate, time: displayTime } = parseEntryDateTime(detailedEntry.entry_date, detailedEntry.created_at);

  const handleDelete = async () => {
    if (!window.confirm(t(LangKeys.delete_entry_confirm))) return;
    try {
      setDeleting(true);
      const res = await deleteEntry(detailedEntry.id);
      if (res?.success) {
        onRefresh();
        onBack();
      } else {
        alert(res?.message || 'Delete failed');
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setDeleting(false);
    }
  };

  const handleShare = () => {
    // Logic to share entry details
    const message = `Entry Details: ${party.name} | ₹${amount.toLocaleString()} | ${displayDate}`;
    if (navigator.share) {
      navigator.share({
        title: 'Entry Details',
        text: message,
        url: window.location.href,
      });
    } else {
      alert(t(LangKeys.share_coming_soon));
    }
  };

  const getWaStatus = () => {
    const status = parseInt(detailedEntry.wa_status || '1');
    switch (status) {
      case 2: return { text: t(LangKeys.wa_message_sent), color: '#16A34A', icon: <CheckCircle size={14} /> };
      case 3: return { text: t(LangKeys.wa_message_failed), color: '#DC2626', icon: <AlertCircle size={14} /> };
      case 4: return { text: t(LangKeys.wa_message_disabled), color: '#64748B', icon: <X size={14} /> };
      case 5: return { text: t(LangKeys.wa_no_credits), color: '#9333EA', icon: <Wallet size={14} /> };
      default: return { text: t(LangKeys.wa_message_pending), color: '#F59E0B', icon: <Clock size={14} /> };
    }
  };

  const waStatus = getWaStatus();

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <h3 style={styles.headerTitle}>{t(LangKeys.entry_details)}</h3>
      </div>

      <div style={styles.scrollContent}>
        {/* AMOUNT CARD (Flutter Style) */}
        <div style={styles.amountCard}>
          <div style={{
            ...styles.typeBadge,
            backgroundColor: isGot ? '#DCFCE7' : '#FEE2E2',
            color: isGot ? '#16A34A' : '#DC2626'
          }}>
            {isGot ? t(LangKeys.you_got) : t(LangKeys.you_gave)}
          </div>
          <h1 style={{
            ...styles.amountText,
            color: isGot ? '#16A34A' : '#DC2626'
          }}>
            {isGot ? '+' : '-'} ₹{amount.toLocaleString()}
          </h1>
        </div>

        {/* DETAILS BOX (Flutter Style) */}
        <div style={styles.detailsBox}>
          <div style={styles.detailRow}>
            <div style={styles.detailIcon}><User size={18} color="#64748B" /></div>
            <div style={styles.detailText}>
              <span style={styles.detailValue}>{party.name}</span>
              <span style={styles.detailLabel}>{party.mobile}</span>
            </div>
          </div>

          <div style={styles.detailRow}>
            <div style={styles.detailIcon}><Calendar size={18} color="#64748B" /></div>
            <div style={styles.detailText}>
              <span style={styles.detailValue}>{displayDate}, {displayTime}</span>
            </div>
          </div>

          {detailedEntry.note && (
            <div style={styles.detailRow}>
              <div style={styles.detailIcon}><FileText size={18} color="#64748B" /></div>
              <div style={styles.detailText}>
                <div style={styles.noteBox}>{detailedEntry.note}</div>
              </div>
            </div>
          )}

          {detailedEntry.entry_image && (
            <div style={styles.detailRow}>
              <div style={styles.detailIcon}><Shield size={18} color="#64748B" /></div>
              <div style={styles.imageWrap} onClick={() => setShowImageModal(true)}>
                <img
                  src={detailedEntry.entry_image.startsWith('http') ? detailedEntry.entry_image : `https://khata.moxpal.com/${detailedEntry.entry_image}`}
                  alt="Entry Attachment"
                  style={styles.attachmentImg}
                />
              </div>
            </div>
          )}

          <div style={styles.detailRow}>
            <div style={styles.detailIcon}><Wallet size={18} color="#64748B" /></div>
            <div style={styles.detailText}>
              <span style={styles.detailValue}>
                {t(LangKeys.running_balance)}:
                <span style={{ color: runningBal >= 0 ? '#16A34A' : '#DC2626', marginLeft: '6px' }}>
                  ₹ {Math.abs(runningBal).toLocaleString()} {runningBal >= 0 ? t(LangKeys.credit) : t(LangKeys.debit)}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* WHATSAPP STATUS (Flutter Style) */}
        <div style={styles.waStatusLine}>
          <div style={{
            ...styles.statusCapsule,
            backgroundColor: `${waStatus.color}14`,
            border: `1px solid ${waStatus.color}40`,
            color: waStatus.color
          }}>
            {waStatus.icon}
            <span style={styles.statusText}>{waStatus.text}</span>
          </div>
        </div>

        {/* BACKUP STATUS */}
        <div style={styles.backupCard}>
          <div style={styles.backupIconWrap}><Shield size={20} color="#16A34A" /></div>
          <div style={styles.backupTextCol}>
            <span style={styles.backupTitle}>{t(LangKeys.entry_is_backed_up)}</span>
            <span style={styles.backupSubtitle}>{t(LangKeys.safe_and_secure)}</span>
          </div>
        </div>
      </div>

      {/* ACTION BUTTONS (Flutter Style) */}
      <div style={styles.bottomBar}>
        <button style={styles.actionBtnSecondary} onClick={handleShare}>
          <Share2 size={18} />
          <span>{t(LangKeys.share)}</span>
        </button>
        <button style={styles.actionBtnPrimary} onClick={() => setShowEditModal(true)}>
          <Edit2 size={18} />
          <span>{t(LangKeys.edit)}</span>
        </button>
        <button
          style={styles.actionBtnDanger}
          onClick={handleDelete}
          disabled={deleting}
        >
          <Trash2 size={18} />
          <span>{deleting ? '...' : t(LangKeys.delete)}</span>
        </button>
      </div>

      {showEditModal && (
        <EditEntryModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          party={party}
          entry={detailedEntry}
          onSuccess={() => {
            setShowEditModal(false);
            onRefresh();
            onBack(); // Go back to ledger to see updated entries
          }}
        />
      )}

      {/* IMAGE MODAL */}
      {showImageModal && (
        <div style={styles.modalOverlay} onClick={() => setShowImageModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button style={styles.closeModal} onClick={() => setShowImageModal(false)}><X size={24} /></button>
            <img
              src={detailedEntry.entry_image.startsWith('http') ? detailedEntry.entry_image : `https://khata.moxpal.com/${detailedEntry.entry_image}`}
              style={styles.fullImg}
              alt="Attachment"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { height: '100%', width: '100%', display: 'flex', flexDirection: 'column', background: '#F3F4F6' },
  header: { height: '60px', background: '#fff', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '16px', borderBottom: '1px solid #E5E7EB' },
  backBtn: { width: '40px', height: '40px', background: '#F1F5F9', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  headerTitle: { fontSize: '17px', fontWeight: 'bold', margin: 0 },

  scrollContent: { flex: 1, overflowY: 'auto', padding: '16px' },

  amountCard: { background: '#fff', borderRadius: '16px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  typeBadge: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' },
  amountText: { fontSize: '22px', fontWeight: 'bold', margin: 0 },

  detailsBox: { background: '#fff', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '18px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  detailRow: { display: 'flex', gap: '14px' },
  detailIcon: { width: '20px', display: 'flex', justifyContent: 'center', paddingTop: '2px' },
  detailText: { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 },
  detailValue: { fontSize: '15px', fontWeight: '700', color: '#1F2937' },
  detailLabel: { fontSize: '13px', color: '#6B7280' },
  noteBox: { padding: '12px', background: '#F9FAFB', borderRadius: '10px', fontSize: '14px', color: '#374151', border: '1px solid #F1F5F9' },

  imageWrap: { borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', border: '1px solid #E5E7EB', width: '80px', height: '80px' },
  attachmentImg: { width: '100%', height: '100%', objectFit: 'cover' },

  waStatusLine: { display: 'flex', justifyContent: 'center', margin: '14px 0' },
  statusCapsule: { padding: '6px 14px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '600' },
  statusText: { textTransform: 'uppercase', letterSpacing: '0.5px' },

  backupCard: { background: '#fff', borderRadius: '14px', padding: '14px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  backupIconWrap: { width: '40px', height: '40px', background: '#DCFCE7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  backupTextCol: { display: 'flex', flexDirection: 'column', gap: '2px' },
  backupTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1F2937' },
  backupSubtitle: { fontSize: '12px', color: '#16A34A', fontWeight: '600' },

  bottomBar: { background: '#fff', padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1.2fr 1fr', gap: '10px', borderTop: '1px solid #E5E7EB' },
  actionBtnSecondary: { height: '44px', borderRadius: '10px', border: '1px solid #E5E7EB', background: '#fff', color: '#1F2937', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' },
  actionBtnPrimary: { height: '44px', borderRadius: '10px', border: 'none', background: '#6366F1', color: '#fff', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' },
  actionBtnDanger: { height: '44px', borderRadius: '10px', border: 'none', background: '#DC2626', color: '#fff', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { position: 'relative', maxWidth: '90%', maxHeight: '90%' },
  closeModal: { position: 'absolute', top: '-40px', right: 0, background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' },
  fullImg: { width: '100%', height: 'auto', borderRadius: '8px' }
};

export default EntryDetailsView;
